'use client';

import { addHeadingIds } from "@/src/utils/html-parser";
import { Box, Stack, Typography } from "@mui/material";
import { faCirclePlay } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import CustomDivider from "@/src/mui-custom/custom-divider/custom-divider";
import DynamicPage from "@/src/components/common/dynamic-page/dynamic-page";
import Grid from "@mui/material/Grid2";
import Hierarchy from "@/src/components/common/hierarchy/hierarchy";
import Journal from "@/src/_types/journal";
import LastUpdate from "@/src/components/common/pages/last-update/last-update";
import Outlines from "@/src/components/common/outlines/outlines";
import PageContentBox from "@/src/components/journal-site/common/page-content/mui-custom/page-content-box/page-content-box";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import SummaryBox from "@/src/mui-custom/summary/summary";
import type Page from "@/src/_types/Page/page";

function HierarchySideBar(
    {
        journal,
        pageSlug,
        page
    }: {
        journal: Journal;
        pageSlug: string;
        page: Page
    }
) {
    const hasChildren = Array.isArray(page?.hierarchy) &&
        page.hierarchy.length > 0 &&
        Array.isArray(page.hierarchy[0]?.childes) &&
        page.hierarchy[0].childes.length > 0;

    return (
        hasChildren &&
        <Box
            position="sticky"
            top={
                journal.is_main_site ? 30 : 120
            }
            mt={{
                xs: 0,
                sm: 0,
                md: 7.5
            }}
        >
            <Hierarchy
                hierarchies={page.hierarchy}
                journal={journal}
                pageSlug={pageSlug}
            />
        </Box>
    );
}

function OutlinesSidebar(
    {
        content,
        journal
    }: {
        content: string;
        journal: Journal;
    }
) {
    return (
        content && (
            <Box
                position="sticky"
                top={
                    journal.is_main_site ? 30 : 120
                }
                mt={{
                    xs: 0,
                    sm: 0,
                    md: 7.5
                }}
            >
                <Outlines
                    content={content}
                    hasAbstract={true}
                    hasBody={false}
                />
            </Box>
        )
    );
}

export default function PageComponent({
    initialJournalData,
    initialPageData,
    pageSlug,
}: {
    initialJournalData: any;
    initialPageData: any;
    journalTitle: string;
    pageSlug: string;
}) {
    const journal = initialJournalData;
    const page = initialPageData;
    const modifiedContent = addHeadingIds(page?.content);

    return (
        <Box
            sx={{
                width: {
                    lg: '100%',
                    xs: '100%'
                }
            }}
            py={{
                xs: 1,
                sm: 1,
                md: 0
            }}
        >
            <Grid
                container
                spacing={2}
                py={{
                    xs: 0,
                    sm: 0,
                    md: 1
                }}
                p={1}
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 2
                    }}
                >
                    <HierarchySideBar
                        page={page}
                        pageSlug={pageSlug}
                        journal={journal}
                    />
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 8
                    }}
                >
                    {
                        page?.title &&
                        <Typography
                            component="h1"
                            mb={{
                                xs: '.5rem',
                                sm: '.5rem',
                                md: 0,
                            }}
                            fontSize={{
                                xs: '1.25rem',
                                sm: '1.5rem',
                                md: '1.75rem'
                            }}
                            marginBottom={{
                                xs: '1rem',
                                sm: '1rem',
                                md: '0rem'
                            }}
                            color={"primary"}
                        >
                            {
                                page.title
                            }
                        </Typography>
                    }
                    <Stack
                        flexDirection={{
                            xs: "column",
                            sm: "column",
                            md: "row",
                        }}
                        alignItems={{
                            xs: "flex-start",
                            sm: "flex-start",
                            md: "right",
                        }}
                        justifyContent={{
                            xs: "center",
                            sm: "center",
                            md: "right",
                        }}
                        mt={0}
                        mb={1}
                        gap={.5}
                    >
                        <SocialIcons
                            url={`${journal?.journal_url}/${page?.slug}`}
                            title={page?.title || ''}
                            direction={'column'}
                        />
                        {
                            page?.updated_at &&
                            <LastUpdate
                                updatedAt={page.updated_at}
                            />
                        }
                    </Stack>
                    <PageContentBox
                        px={{
                            xs: 1,
                            sm: 1,
                            md: 3
                        }}
                        py={{
                            xs: 1,
                            sm: 1,
                            md: 1
                        }}
                        mb={{
                            xs: 1,
                            sm: 1,
                            md: 3
                        }}
                    >
                        {
                            page?.summary &&
                            <SummaryBox id="page-summary">
                                <Typography
                                    style={{
                                        margin: '0 !important',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    <FontAwesomeIcon
                                        color={'main'}
                                        style={{
                                            verticalAlign: 'middle',
                                            marginRight: '0.25rem'
                                        }}
                                        icon={faCirclePlay}
                                    />
                                    {
                                        stripHTMLTags(page.summary, ['span'])
                                    }
                                </Typography>
                            </SummaryBox>
                        }
                        <DynamicPage
                            htmlContent={modifiedContent}
                        />
                    </PageContentBox>
                </Grid>
                <Grid
                    size={
                        {
                            xs: 12,
                            sm: 12,
                            md: 2
                        }
                    }
                    display={{
                        xs: 'none',
                        sm: 'none',
                        md: 'block'
                    }}
                >
                    <OutlinesSidebar
                        content={modifiedContent}
                        journal={journal}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
